import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid } from '@mui/material';
import { Bar, LinePath } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import Truncate from 'react-truncate';
import PropTypes from 'prop-types';

import { getParams, thousandFormat2 } from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  barThinHeight,
  formatedStyle,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const MiddleChart = ({
  middleData,
  selectedTopItems,
  maxCashFlowGroupName,
  maxCashFlowCategoryName,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedBarObjs, setSelectedBarObjs] = useState([]);
  const [data, setData] = useState([]);
  const [group, setGroup] = useState([]);

  const isMountedRef = useRef(true);

  // Force re-render when window is resized
  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const prepareData = useCallback(() => {
    let dataObj = [],
      _data = [];
    let groupObj = {},
      _group = [];

    if (middleData) {
      middleData.forEach((item) => {
        if (item.CashFlowGroup && item.CashFlowCategory && item.CashChange !== 0) {
          if (dataObj[item.CashFlowCategory] === undefined) {
            dataObj[item.CashFlowCategory] = {};
            dataObj[item.CashFlowCategory]['CashChange'] = 0;
          }
          dataObj[item.CashFlowCategory]['CashFlowGroup'] = item.CashFlowGroup;
          dataObj[item.CashFlowCategory]['CashFlowCategory'] =
            item.CashFlowCategory;
          dataObj[item.CashFlowCategory]['CashChange'] += parseFloat(
            item.CashChange,
          );
        }
      });
    }

    Object.keys(dataObj).forEach((key) => {
      if (dataObj[key].ForecastCashChange !== 0) {
        _data.push(dataObj[key]);
      }
    });

    _data.sort((a, b) => b.CashChange - a.CashChange);

    _data.forEach((item) => {
      if (groupObj[item.CashFlowGroup] === undefined)
        groupObj[item.CashFlowGroup] = {};
      groupObj[item.CashFlowGroup] = item.CashFlowGroup;
    });

    Object.keys(groupObj).map((key) => _group.push(key));    
    setData(_data);
    setGroup(_group);
  }, [middleData]);

  const getColor = useCallback((bar) => {
    if (bar.CashChange === undefined) return positiveActiveColor;

    let activeColor;
    let disableColor;
    if (bar.CashChange > 0) {
      activeColor = positiveActiveColor;
      disableColor = positiveDisableColor;
    } else {
      activeColor = negativeActiveColor;
      disableColor = negativeDisableColor;
    }

    let index = -1;
    if (selectedBarObjs && selectedBarObjs.length !== 0) {
      selectedBarObjs.forEach((item) => {
        if (item.AccountName === bar.AccountName) {
          index++;
        }
      });
    } else {
      return activeColor;
    }

    if (index >= 0) return activeColor;

    return disableColor;
  }, [selectedBarObjs]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedBarObjs;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBarObjs = selectedBarObjs.slice();

      let index = -1;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (index === -1) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBarObjs.push(bar);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBarObjs.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBars = [];
        _selectedBarObjs = [];
      } else {
        _selectedBars = [event.target];
        _selectedBarObjs = [bar];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);
    setSelectedBarObjs(_selectedBarObjs);
  }, [selectedBars, selectedBarObjs]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    setSelectedBarObjs([]);
  }, [hoverBar, selectedBars]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let dataObj = {},
      top,
      left;

    top = event.clientY - 100;
    left =
      window.innerWidth / 2 < event.clientX
        ? event.clientX - 340
        : event.clientX + 60;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      dataObj = bar;
    } else {
      dataObj['forecast'] = bar;
    }

    dataObj['isBar'] = isBar;

    showTooltip({
      tooltipData: dataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize, prepareData]);

  useEffect(() => {
    if (isMountedRef.current) {
      prepareData();
    }
  }, [middleData, selectedTopItems, prepareData]);

  // Render logic
  const width = window.innerWidth - 5;
  const height = 300;
  const margin = {
    top: 0,
    right: 0,
    bottom: 20,
    left: 10,
  };

  const { xMax } = getParams(window, width, height, margin);

  let minData = 0,
    maxData = 0;

  data.forEach((item) => {
    minData += item.CashChange;
    maxData = Math.max(minData, maxData);
    maxData = Math.max(item.CashChange, maxData);
  });

  if (minData > 0) minData = 0;

  let axisWidth =
    window.innerWidth -
    (maxCashFlowGroupName + maxCashFlowCategoryName) * 6.2 -
    30;

  const xScale = scaleLinear({
    range: [0, axisWidth],
    domain: [minData, maxData],
    nice: true,
  });
  let _maxData = 0;
  let _minData = 0;
  let heightPercent = 30;

  if (document.getElementById('cashflow-middle-root')) {
    let middleHeight = 20 * data.length + 60;
    if (Math.ceil((middleHeight / window.innerHeight) * 100) + 1 > 30) {
      if (Math.ceil((middleHeight / window.innerHeight) * 100) + 1 > 50)
        heightPercent = 50;
      else
        heightPercent =
          Math.ceil((middleHeight / window.innerHeight) * 100) + 1;
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: `${heightPercent}vh`,
      }}
      id="cashflow-middle-root"
    >
      <div>
        <div>
          <div className="subtitle mt-10 mb-5">Selected Cash Flow</div>
        </div>

        <div>
          <div style={formatedStyle(xMax, 12, 0, 0, 'pointer')} id="wrapper">
            <Grid
              container
              style={{
                overflow: 'hidden auto',
                maxHeight: `${heightPercent}vh`,
              }}
            >
              {group.map((grupItem, i) => {
                const yMax = barThinHeight - 5;
                if (
                  data.filter((item) => item.CashFlowGroup === grupItem)
                    .length === 0
                )
                  return null;
                return (
                  <Grid
                    container
                    key={i}
                    style={{
                      borderTop: 'solid 1px #a9a9a9',
                    }}
                  >
                    <div
                      style={{
                        width: maxCashFlowGroupName * 6.2,
                      }}
                    >
                      <p
                        style={{
                          paddingLeft: 2,
                          margin: '2.5px 0 0',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Truncate
                          ellipsis={<span>...</span>}
                        >
                          {grupItem}
                        </Truncate>
                      </p>
                    </div>

                    <div
                      style={{
                        width:
                          window.innerWidth - maxCashFlowGroupName * 6.2 - 20,
                      }}
                    >
                      {data.map((item, j) => {
                        if (item.CashFlowGroup !== grupItem) return null;
                        let barX, barY, barWidth, barHeight;
                        barY = 1;
                        barHeight = 18;

                        if (item.CashChange < 0) {
                          _maxData += item.CashChange;
                          barX = xScale(_maxData);
                          barWidth = xScale(_minData) - barX;
                          _minData += item.CashChange;
                        } else {
                          _maxData += item.CashChange;
                          barX = xScale(_minData);
                          barWidth = xScale(_maxData) - barX;
                          _minData += item.CashChange;
                        }

                        return (
                          <Grid container key={j}>
                            <div
                              style={{
                                width: maxCashFlowCategoryName * 6.2,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <p
                                style={{
                                  paddingLeft: 2,
                                  margin: 0,
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Truncate
                                  ellipsis={<span>...</span>}
                                >
                                  {item.CashFlowCategory}
                                </Truncate>
                              </p>
                            </div>
                            <div
                              style={{
                                width:
                                  window.innerWidth -
                                  (maxCashFlowGroupName +
                                    maxCashFlowCategoryName) *
                                    6.2 -
                                  20,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <svg height={yMax} width={axisWidth}>
                                <rect
                                  x={0}
                                  y={0}
                                  height={yMax}
                                  fill="transparent"
                                  onClick={deSelectAll}
                                />
                                <Bar
                                  x={barX}
                                  y={barY}
                                  width={barWidth}
                                  height={barHeight}
                                  fill={getColor(item)}
                                  onClick={(event) =>
                                    handleBar(event, item)
                                  }
                                  onMouseLeave={(event) =>
                                    hideTooltipHandler()
                                  }
                                  onMouseMove={(event) =>
                                    showTooltipHandler(event, xScale, item)
                                  }
                                  onTouchEnd={(event) => hideTooltipHandler()}
                                  onTouchMove={(event) =>
                                    showTooltipHandler(event, xScale, item)
                                  }
                                ></Bar>

                                <LinePath
                                  data={[
                                    { x: xScale(0), y: 0 },
                                    { x: xScale(0), y: yMax },
                                  ]}
                                  x={(d) => (d.x ? d.x : 0)}
                                  y={(d) => (d.y ? d.y : 0)}
                                  stroke="#555555"
                                  strokeDasharray="6,2"
                                />
                              </svg>
                            </div>
                          </Grid>
                        );
                      })}
                    </div>
                  </Grid>
                );
              })}
            </Grid>
            {data.length > 0 ? (
              <div
                style={{
                  height: 28,
                  borderTop: 'solid 1px #a9a9a9',
                }}
                onClick={deSelectAll}
              >
                <svg
                  width={window.innerWidth - 15}
                  height={barThinHeight}
                  style={{ overflow: 'visible' }}
                >
                  <AxisBottom
                    scale={xScale}
                    top={0}
                    left={
                      (maxCashFlowGroupName + maxCashFlowCategoryName) * 6.2
                    }
                    hideAxisLine={true}
                    stroke="black"
                    numTicks={10}
                    tickStroke="#a9a9a9"
                    tickFormat={(value, index) => value / 1000}
                    tickLabelProps={(value, index) => ({
                      fill: 'black',
                      fontSize: 11,
                      dy: '0.2em',
                      dx: '-2em',
                    })}
                    tickComponent={({ formattedValue, ...tickProps }) => (
                      <text {...tickProps}>
                        $
                        {Math.abs(formattedValue) < 1000
                          ? `${formattedValue}K`
                          : `${formattedValue / 1000}M`}
                      </text>
                    )}
                  />
                </svg>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="color-black" style={{ width: tooltipData.CashFlowGroup.length * 8.5 }}>
              <div className="pdv-10">
                <strong>{tooltipData.CashFlowGroup}: </strong>
                <strong>{tooltipData.CashFlowCategory}</strong>
              </div>
              <div className="flex">
                <strong> {thousandFormat2(tooltipData.CashChange)}</strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

MiddleChart.propTypes = {
  middleData: PropTypes.array.isRequired,
  maxCashFlowGroupName: PropTypes.number.isRequired,
  maxCashFlowCategoryName: PropTypes.number.isRequired,
  selectedTopItems: PropTypes.array,
  handleFilter: PropTypes.func,
};

export default MiddleChart;
